import { api } from "./api";

export interface UserSettings {
  profile?: {
    bio?: string;
    headline?: string;
    website?: string;
    linkedIn?: string;
    twitter?: string;
    github?: string;
    youtube?: string;
  };
  notifications?: {
    email?: {
      courseUpdates?: boolean;
      newCourses?: boolean;
      promotions?: boolean;
      communityActivity?: boolean;
      weeklyDigest?: boolean;
    };
    push?: {
      newMessages?: boolean;
      courseAnnouncements?: boolean;
      newReviews?: boolean;
      mentorshipReminders?: boolean;
    };
    inApp?: {
      showOnlineStatus?: boolean;
      showActivityStatus?: boolean;
    };
  };
  privacy?: {
    profileVisibility?: "public" | "students" | "connections" | "private";
    showProgress?: boolean;
    showCertificates?: boolean;
    allowMessaging?: boolean;
    showInLeaderboard?: boolean;
  };
  learning?: {
    preferredLanguage?: string;
    playbackSpeed?: number;
    autoPlayNext?: boolean;
    showCaptions?: boolean;
    theme?: "light" | "dark" | "system";
  };
  security?: {
    twoFactorEnabled?: boolean;
    twoFactorMethod?: "email" | "authenticator";
    loginAlerts?: boolean;
    sessionTimeout?: number;
  };
  accessibility?: {
    fontSize?: "small" | "medium" | "large";
    highContrast?: boolean;
    screenReaderOptimized?: boolean;
  };
}

const defaultSettings: UserSettings = {
  profile: {},
  notifications: {
    email: {
      courseUpdates: true,
      newCourses: true,
      promotions: false,
      communityActivity: true,
      weeklyDigest: true,
    },
    push: {
      newMessages: true,
      courseAnnouncements: true,
      newReviews: true,
      mentorshipReminders: true,
    },
    inApp: {
      showOnlineStatus: true,
      showActivityStatus: true,
    },
  },
  privacy: {
    profileVisibility: "students",
    showProgress: true,
    showCertificates: true,
    allowMessaging: true,
    showInLeaderboard: true,
  },
  learning: {
    preferredLanguage: "en",
    playbackSpeed: 1,
    autoPlayNext: false,
    showCaptions: false,
    theme: "system",
  },
  security: {
    twoFactorEnabled: false,
    twoFactorMethod: "email",
    loginAlerts: true,
    sessionTimeout: 30,
  },
  accessibility: {
    fontSize: "medium",
    highContrast: false,
    screenReaderOptimized: false,
  },
};

export const userSettingsApi = {
  getSettings: () => {
    return api.get<{ success: boolean; data: UserSettings }>("/user-settings");
  },

  updateSettings: (settings: Partial<UserSettings>) => {
    return api.put<{ success: boolean; message: string; data: UserSettings }>("/user-settings", settings);
  },

  updateProfile: (profile: Partial<UserSettings["profile"]>) => {
    return api.put<{ success: boolean; message: string }>("/user-settings/profile", profile);
  },

  updateNotifications: (notifications: Partial<UserSettings["notifications"]>) => {
    return api.put<{ success: boolean; message: string }>("/user-settings/notifications", notifications);
  },

  updatePrivacy: (privacy: Partial<UserSettings["privacy"]>) => {
    return api.put<{ success: boolean; message: string }>("/user-settings/privacy", privacy);
  },

  updateLearning: (learning: Partial<UserSettings["learning"]>) => {
    return api.put<{ success: boolean; message: string }>("/user-settings/learning", learning);
  },

  updateSecurity: (security: Partial<UserSettings["security"]>) => {
    return api.put<{ success: boolean; message: string }>("/user-settings/security", security);
  },

  updateAccessibility: (accessibility: Partial<UserSettings["accessibility"]>) => {
    return api.put<{ success: boolean; message: string }>("/user-settings/accessibility", accessibility);
  },

  resetSettings: () => {
    return api.post<{ success: boolean; message: string }>("/user-settings/reset");
  },

  deleteAccount: (password: string) => {
    return api.delete<{ success: boolean; message: string }>("/user-settings/account", { password });
  },
};

export { defaultSettings };